import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  memo,
  useMemo,
  useCallback,
} from "react";
// import * as PIXI from "pixi.js"; // Not needed anymore
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectAutoPlaySpeed,
  selectBet,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
} from "../../../State/selectors";
import ReelControllerV2 from "./ReelControllerV2"; // New controller
import { safeInit } from "../../../utilities";
import { Settings } from "../../../settings";
import DimReelOverlay from "../DimReelOverlay/DimReelOverlay";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to skip spin complete delay for trigger rounds.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

const Reel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
      },
      ref,
    ) => {
      const dispatch = useDispatch();
      const reelController = useRef(null);
      const symbolsContainerRef = useRef(null);
      const dimOverlayRef = useRef(null);
      const currentRound = useSelector(selectCurrentRound);
      const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

      // Use free rounds bet when active, otherwise use regular bet - memoized
      const freeRoundsActive = useSelector(selectFreeRoundsActive);
      const freeRoundsBet = useSelector(selectFreeRoundsBet);
      const regularBet = useSelector(selectBet);
      const bet = useMemo(
        () =>
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
          freeRoundsActive &&
          freeRoundsBet
            ? freeRoundsBet
            : regularBet,
        [freeRoundsActive, freeRoundsBet, regularBet],
      );

      // Landing animations hook
      const { triggerReelLandingAnimations } = useReelLandingAnimations();

      // 🎮 Initialize controller once
      useEffect(() => {
        if (symbolsContainerRef.current && !reelController.current) {
          safeInit(() => {
            reelController.current = new ReelControllerV2(
              dispatch,
              symbolsContainerRef.current,
              symbolsSpriteSheet,
              symbolsSpriteSheetBlurred,
              reel, // Initial symbols from API (currentRound.reels)
              index, // reelIndex
              symbolWidth,
              symbolHeight,
              triggerReelLandingAnimations, // Callback for landing animations
              autoPlaySpeed, // Current autoplay speed
              bet, // Current bet value
            );
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Update reels when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;
        const reelData = currentRound?.reels?.[index];
        reelController.current.updateReels(reelData);

        // Update win state and trigger state for last reel only
        if (index === Settings.REEL_COUNT - 1) {
          // Update win state for sound selection
          reelController.current.setHasWinThisSpin?.(currentRound?.win > 0);

          // Update Hold & Win trigger state to skip spin complete delay
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          reelController.current.setIsHoldAndWinTrigger?.(isHoldAndWinTrigger);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound]);

      // 📏 Resize handler
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.resizeSymbol(symbolWidth, symbolHeight);
      }, [symbolWidth, symbolHeight]);

      // ⚡ Auto play speed update
      useEffect(() => {
        if (
          reelController.current &&
          reelController.current.autoPlaySpeed !== autoPlaySpeed
        ) {
          reelController.current.autoPlaySpeed = autoPlaySpeed;
        }
      }, [autoPlaySpeed]);

      // 💰 Bet update - update coin values when bet changes
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.updateBet(bet);
      }, [bet]);

      // 🧹 Cleanup
      useEffect(() => {
        return () => {
          if (!reelController.current) return;

          // Clear all animations, timeouts, and PIXI Ticker callbacks
          reelController.current.clearAllTimeouts();

          // Destroy all spinning sprites and their PIXI objects (textures, containers)
          reelController.current._cleanupSpinningSprites();

          // Reset controller state on unmount
          reelController.current.isStopping = false;
          reelController.current.hasStopped = true;
          reelController.current.spinStartTime = null;
        };
      }, []);

      // Memoize controller methods
      const startSpin = useCallback(() => {
        reelController.current?.spin();
      }, []);

      const startSpinNoWindUp = useCallback(() => {
        reelController.current?.spinNoWindUp();
      }, []);

      const stopSpin = useCallback(() => {
        reelController.current?.stopSpin();
      }, []);

      const startTurboSpin = useCallback(() => {
        reelController.current?.spinTurbo();
      }, []);

      const stopTurboSpin = useCallback(() => {
        reelController.current?.stopSpinTurbo();
      }, []);

      const startTensionSpin = useCallback(() => {
        reelController.current?.spinTension();
      }, []);

      const stopTensionSpin = useCallback(() => {
        reelController.current?.stopSpinTension();
      }, []);

      const freezeSpin = useCallback(() => {
        reelController.current?.freezeSpin();
      }, []);

      const restoreToIdleState = useCallback((symbolIds, betValue = null) => {
        reelController.current?.restoreToIdleState(symbolIds, betValue);
      }, []);

      const setOnReelLanded = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onReelLanded = callback;
        }
      }, []);

      const getReelController = useCallback(() => {
        return reelController.current;
      }, []);

      const getReelSpinComplete = useCallback(() => {
        return reelController.current?.hasStopped ?? false;
      }, []);

      const replaceVisibleSymbols = useCallback(
        (targetSymbolId, hideTextures = true) => {
          reelController.current?.replaceVisibleSymbols(
            targetSymbolId,
            hideTextures,
          );
        },
        [],
      );

      const replaceSymbolAtPosition = useCallback(
        (symbolIndex, targetSymbolId) => {
          reelController.current?.replaceSymbolAtPosition(
            symbolIndex,
            targetSymbolId,
          );
        },
        [],
      );

      const dimReel = useCallback(() => {
        dimOverlayRef.current?.dimReel();
      }, []);

      const undimReel = useCallback(() => {
        dimOverlayRef.current?.undimReel();
      }, []);

      // 🎛️ Expose imperative API for external reel control
      useImperativeHandle(
        ref,
        () => ({
          startSpin,
          startSpinNoWindUp,
          stopSpin,

          // Turbo mode (instant drop, no wind-up)
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,

          // Freeze spin instantly (for error popups)
          freezeSpin,

          // Restore reel to idle state with specific symbols (for error recovery)
          restoreToIdleState,

          // Set callback for when reel has fully landed (bounce complete)
          setOnReelLanded,

          getReelController,
          getReelSpinComplete,

          // Expanding wild support
          replaceVisibleSymbols,
          replaceSymbolAtPosition,

          // Dim control methods for tension mode and other effects
          dimReel,
          undimReel,
        }),
        [
          startSpin,
          startSpinNoWindUp,
          stopSpin,
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,
          freezeSpin,
          restoreToIdleState,
          setOnReelLanded,
          getReelController,
          getReelSpinComplete,
          replaceVisibleSymbols,
          replaceSymbolAtPosition,
          dimReel,
          undimReel,
        ],
      );

      return (
        <>
          {/* Container for programmatically created sprites (no React components) */}
          <Container
            ref={symbolsContainerRef}
            name={`reel_${index}`}
            y={0}
            x={0}
          />
          {FEATURES_CONFIG.TENSION.ENABLED && (
            <DimReelOverlay
              ref={dimOverlayRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
            />
          )}
        </>
      );
    },
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index,
  // Note: currentRound changes are handled by useEffect, not memo
);

export default Reel;
